import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  Image,
  Text,
} from '@aboutyou/ui-components';
import React, {FC, useRef, useState} from 'react';
import {useRectEffect} from 'react-viewport-utils';
import styled, {css} from 'styled-components';
import qrcode from '../assets/qrcode.svg';
import {ANIMATION_DURATION_TIME} from './constants';

const ESTIMATED_CODE_TEXT_WIDTH = 133;
export const paddingCssVariable = '--app-download-right-padding';
const QR_CODE_HEIGHT_WHEN_HOVER = 72;
const QR_CODE_LEFT_PADDING = 35;

export const DownloadAppQr: FC<{isDisabled?: boolean; className?: string}> = ({
  isDisabled,
  className,
}) => {
  const downloadTextRef = useRef<HTMLDivElement>(null);
  const {t} = useTranslation();
  const [downloadTextWidth, updateDownloadTextWidth] = useState(
    ESTIMATED_CODE_TEXT_WIDTH,
  );

  useRectEffect(
    rect => {
      updateDownloadTextWidth(rect?.width || ESTIMATED_CODE_TEXT_WIDTH);
    },
    downloadTextRef,
    {
      disableScrollUpdates: true,
      priority: 'low',
    },
  );

  return (
    <QRDownloadWrapper className={className}>
      <QRCode
        $downloadAppTextWidth={downloadTextWidth}
        src={qrcode}
        breakpoints={[
          {
            columns: 1,
          },
        ]}
        ratio="1:1"
        widths={[100, 200]}
        $isDisabled={isDisabled}
      />
      <DownloadText ref={downloadTextRef} $isDisabled={isDisabled}>
        {t('Download App')}
        <Icon
          asset={GenericIconName.ArrowUpSharpCorners}
          size="S"
          fill={COLORS.PrimaryCoupon}
        />
      </DownloadText>
      <ScanQRText
        weight="mediumHeavy"
        color="PrimaryCoupon"
        $isDisabled={isDisabled}
      >
        {t('Scan QR code and Download App')}
      </ScanQRText>
    </QRDownloadWrapper>
  );
};

const QRDownloadWrapper = styled.div`
  --transition-duration: ${ANIMATION_DURATION_TIME}ms;
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: end;
`;

export const QRCode = styled(Image)<{
  $downloadAppTextWidth: number;
  $isDisabled?: boolean;
}>`
  height: 20px;
  width: auto;
  position: absolute;
  // the qr code is moved to the right by the amount of text height, download icon width
  // 8px of spacing
  ${({$downloadAppTextWidth}) => css`
    right: calc(
      ${$downloadAppTextWidth}px + 8px + var(${paddingCssVariable}, 0px)
    );
  `}

  // eslint-disable-next-line local-rules/css-layout-animation
  transition: height var(--transition-duration) ease-in-out,
    transform var(--transition-duration) ease-in-out,
    right var(--transition-duration) ease-in-out;

  ${({$isDisabled}) =>
    $isDisabled &&
    css`
      filter: grayscale(0.5) contrast(0.3);
    `}
`;

const FloatingText = css`
  align-items: center;
  display: flex;
  font-size: ${FONT_SIZES.px12};
`;

export const DownloadText = styled.div<{$isDisabled?: boolean}>`
  ${FloatingText}
  font-weight: ${FONT_WEIGHTS.mediumBold};
  color: ${COLORS.PrimaryCoupon};

  opacity: 1;
  transition: opacity var(--transition-duration) ease-in-out,
    transform var(--transition-duration) ease-in-out;

  ${({$isDisabled}) =>
    $isDisabled &&
    css`
      color: ${COLORS.Grey1};
    `}
`;

export const ScanQRText = styled(Text)<{$isDisabled?: boolean}>`
  ${FloatingText}

  position: absolute;
  opacity: 0;
  // 16px of padding between the text and the QR code
  width: calc(
    100% - ${QR_CODE_HEIGHT_WHEN_HOVER}px - ${QR_CODE_LEFT_PADDING}px - 16px
  );
  right: 0;
  padding-right: 16px;
  transform: translate(40%, 40%);

  transition: opacity calc(var(--transition-duration) + 20ms) ease-in-out,
    transform calc(var(--transition-duration) + 20ms) ease-in-out;

  ${({$isDisabled}) =>
    $isDisabled &&
    css`
      color: ${COLORS.Grey1};
    `}
`;
